/**
 * Когда филиал открыт.
 *
 * Нужно двоим и по-разному: боту — чтобы не звать человека к закрытой двери
 * («подходите, работаем до 21:00» в полночь читается как издёвка), и самому
 * клиенту — чтобы знать, когда приходить. Отсюда две функции: одна отвечает
 * «открыто ли сейчас», вторая говорит часы словами.
 *
 * Время хранится строкой `ЧЧ:ММ` в поясе филиала, а не меткой времени: «с
 * десяти до девяти» — правило, а не событие, и от перевода часов оно не
 * зависит. Пояс у точки уже есть (`points.timezone`).
 */

/** Дни недели с воскресенья: так их нумерует `Date.getDay()` */
export const WEEKDAYS = [
  { key: 'sun', short: 'Вс', name: 'воскресенье' },
  { key: 'mon', short: 'Пн', name: 'понедельник' },
  { key: 'tue', short: 'Вт', name: 'вторник' },
  { key: 'wed', short: 'Ср', name: 'среда' },
  { key: 'thu', short: 'Чт', name: 'четверг' },
  { key: 'fri', short: 'Пт', name: 'пятница' },
  { key: 'sat', short: 'Сб', name: 'суббота' },
] as const

export type WeekdayKey = (typeof WEEKDAYS)[number]['key']

/** Рабочий день: с какого по какое. `null` — выходной */
export interface WorkDay {
  from: string
  to: string
}

export type WorkHours = Partial<Record<WeekdayKey, WorkDay | null>>

/**
 * Исключение из графика: праздник, сокращённый день, инвентаризация.
 *
 * Датой, а не правилом: «31 декабря до 16:00» повторяется каждый год, но
 * решение об этом принимают каждый год заново, и прошлогоднее «закрыто»
 * не должно тихо закрывать магазин в этом.
 */
export interface ScheduleException {
  /** `ГГГГ-ММ-ДД` в поясе филиала */
  date: string
  /** `null` — закрыто весь день */
  hours: WorkDay | null
  note?: string | null
}

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/

export const isWorkTime = (value: unknown): value is string =>
  typeof value === 'string' && TIME.test(value)

/** Минуты от полуночи: сравнивать строки «09:30» и «10:00» нельзя */
const minutes = (time: string): number => {
  const [h, m] = time.split(':')
  return Number(h) * 60 + Number(m)
}

/**
 * Что за день у филиала в его часовом поясе.
 *
 * Пояс обязателен: магазин в Челябинске закрывается в девять по своему
 * времени, а сервер живёт по своему — и без пересчёта бот считал бы вечер
 * ночью на два часа раньше.
 */
export function localParts(at: Date, timezone: string): { date: string; weekday: WeekdayKey; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false,
  }).formatToParts(at)

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''

  const weekdayByShort: Record<string, WeekdayKey> = {
    Sun: 'sun',
    Mon: 'mon',
    Tue: 'tue',
    Wed: 'wed',
    Thu: 'thu',
    Fri: 'fri',
    Sat: 'sat',
  }

  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    weekday: weekdayByShort[get('weekday')] ?? 'mon',
    /* 24:00 у полуночи — законный ответ Intl, а для сравнения он должен быть
       нулём, иначе полночь считается концом суток */
    time: `${get('hour') === '24' ? '00' : get('hour')}:${get('minute')}`,
  }
}

export interface OpenState {
  /** Открыто ли прямо сейчас */
  open: boolean
  /** Часы сегодня: `null` — сегодня выходной */
  today: WorkDay | null
  /** Часы ближайшего рабочего дня, если сейчас закрыто */
  next: { day: WeekdayKey; hours: WorkDay } | null
}

/**
 * Открыт ли филиал в этот момент.
 *
 * Исключение по дате перекрывает недельное правило целиком: в праздник
 * магазин работает не «как в среду», а как решили на этот день.
 *
 * График не заполнен — считаем, что открыт. Молчать из-за незаполненного
 * поля хуже, чем ответить: до появления графика бот работал круглосуточно, и
 * пустая настройка не должна менять поведение задним числом.
 */
export function openState(
  input: {
    hours: WorkHours | null
    exceptions?: ScheduleException[]
    timezone: string
  },
  at: Date = new Date(),
): OpenState {
  const { date, weekday, time } = localParts(at, input.timezone)
  const hours = input.hours ?? {}

  if (Object.keys(hours).length === 0) {
    return { open: true, today: null, next: null }
  }

  const exception = input.exceptions?.find((one) => one.date === date)
  const today = exception ? exception.hours : (hours[weekday] ?? null)

  const now = minutes(time)
  const open = Boolean(today && now >= minutes(today.from) && now < minutes(today.to))

  if (open) return { open, today, next: null }

  /* Ближайший рабочий день — включая сегодняшний, если до открытия ещё есть
     время: «откроемся в десять» полезнее, чем «работаем завтра» */
  if (today && now < minutes(today.from)) {
    return { open, today, next: { day: weekday, hours: today } }
  }

  const order = WEEKDAYS.map((d) => d.key)
  const start = order.indexOf(weekday)

  for (let step = 1; step <= 7; step += 1) {
    const key = order[(start + step) % 7]!
    const day = hours[key]
    if (day) return { open, today, next: { day: key, hours: day } }
  }

  return { open, today, next: null }
}

/** «10:00–21:00» либо «выходной» */
export const dayLabel = (day: WorkDay | null): string =>
  day ? `${day.from}–${day.to}` : 'выходной'

/**
 * График словами, свернув одинаковые дни: «Пн–Пт 10:00–21:00, Сб 10:00–18:00,
 * Вс выходной».
 *
 * Свернув — потому что семь строк подряд человек не читает, а в сообщении
 * клиенту они занимают весь экран телефона.
 */
export function hoursLabel(hours: WorkHours | null): string {
  if (!hours || Object.keys(hours).length === 0) return ''

  /* Порядок с понедельника: неделя у человека начинается с него, хотя
     `Date.getDay()` считает иначе */
  const week = [...WEEKDAYS.slice(1), WEEKDAYS[0]]
  const groups: { from: string; to: string; label: string }[] = []

  for (const day of week) {
    const label = dayLabel(hours[day.key] ?? null)
    const last = groups.at(-1)

    if (last && last.label === label) last.to = day.short
    else groups.push({ from: day.short, to: day.short, label })
  }

  return groups
    .map((one) => `${one.from === one.to ? one.from : `${one.from}–${one.to}`} ${one.label}`)
    .join(', ')
}

/** Дневной набор к виду, который принимает база: чужие ключи и время отбиты */
export function normalizeHours(input: unknown): WorkHours | null {
  if (!input || typeof input !== 'object') return null

  const result: WorkHours = {}

  for (const day of WEEKDAYS) {
    const raw = (input as Record<string, unknown>)[day.key]
    if (raw === undefined) continue

    if (raw === null) {
      result[day.key] = null
      continue
    }

    const value = raw as Record<string, unknown>
    if (!isWorkTime(value.from) || !isWorkTime(value.to)) continue
    /* Закрытие раньше открытия — не ночная смена, а опечатка: ночных смен у
       скупки не бывает, а «с 21:00 до 10:00» закрыло бы магазин на день */
    if (minutes(value.from) >= minutes(value.to)) continue

    result[day.key] = { from: value.from, to: value.to }
  }

  return Object.keys(result).length > 0 ? result : null
}

/**
 * Когда филиал откроется — абсолютным временем.
 *
 * Нужно тому, кто ставит срок: напоминание и прощание уходят клиенту в
 * мессенджер, и «через пять суток» попадает в ту же минуту, в какую стадию
 * поставили. Стадию ставят в 23:40 — сообщение уходило в 23:40, а на
 * неофициальном номере ночная рассылка это прямой путь к блокировке.
 *
 * Открыто сейчас — возвращается само время; закрыто — ближайшее открытие в
 * поясе филиала. Расписания нет вовсе — «круглосуточно», и время не
 * двигается: так же считает `openState`.
 */
export function nextOpenAt(
  input: { hours: WorkHours | null; exceptions?: ScheduleException[]; timezone: string },
  from: Date,
): Date {
  if (!input.hours) return from

  /* Ищем по дням вперёд: неделя с запасом на праздники, которые закрывают
     филиал подряд. Не нашли — оставляем как есть, молчать хуже, чем
     написать в неурочный час */
  if (openState(input, from).open) return from

  for (let shift = 0; shift <= 14; shift += 1) {
    const at = new Date(from.getTime() + shift * 86_400_000)

    /* Часы этого дня с учётом исключения. `next` из `openState` не годится:
       он смотрит вперёд от «сейчас» */
    const local = localParts(at, input.timezone)
    const exception = input.exceptions?.find((one) => one.date === local.date)
    const day = exception ? exception.hours : (input.hours[local.weekday] ?? null)

    if (!day) continue

    /* Сегодня открытие уже было — ждать в этом дне нечего. На следующих днях
       время суток `at` то же, что у `from`, и сдвиг к открытию бывает
       отрицательным: раньше такие дни пропускались, и после вечернего
       закрытия функция возвращала ту же минуту — сообщение, поставленное в
       23:40, уходило в 23:40 */
    const wait = minutes(day.from) - minutes(local.time)
    if (shift === 0 && wait <= 0) continue

    return new Date(at.getTime() + wait * 60_000)
  }

  return from
}
